import PropTypes from 'prop-types';
import { numberFormat, unitToString } from '@/utils/unitUtils';
import AnalysisContainer from '../core/AnalysisContainer';
import AnalysisBox from '../core/AnalysisBox';

const getCIColorClass = ci =>
  ci < 30 ? 'bg-green-100 text-green-800' : ci < 60 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800';

const DetailRow = ({ label, value }) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs text-gray-600 uppercase">{label}</span>
    <span className="text-xs font-medium text-gray-900">{value}</span>
  </div>
);

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

const PathwayDescription = ({ pathway }) => {
  const { cost, emissions, description = '', feedstocks, locations, frameworks } = pathway;
  const totalCost = numberFormat(cost.scalar);
  const totalEmissions = numberFormat(emissions.scalar);
  const costUnit = unitToString(cost.unit);
  const emissionUnit = unitToString(emissions.unit);
  const details = [
    { label: 'Feedstock', value: feedstocks?.[0] ?? '-' },
    { label: 'Region', value: locations?.length ? locations.join(', ') : '-' },
    { label: 'Framework', value: frameworks?.length ? frameworks.join(', ') : '-' },
  ];

  return (
    <AnalysisContainer title="Summary">
      <AnalysisBox>
        <div className="grid grid-cols-1 gap-4">
          <div className="grid grid-cols-6 gap-4">
            <div className="col-span-4 gap-2 flex flex-col">
              <h5 className="text-sm font-semibold text-gray-900">Description</h5>
              <p className="text-gray-600 text-sm leading-relaxed mb-6">{description}</p>
            </div>
            <div className="col-span-2 gap-2 flex flex-col">
              <h5 className="text-sm font-semibold text-gray-900">Key Metrics</h5>
              <div className="space-y-4">
                <div className="text-center">
                  <div
                    className={`inline-block px-4 py-2 rounded-2xl text-sm font-semibold ${getCIColorClass(totalEmissions)}`}
                  >
                    {totalEmissions} {emissionUnit}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">Carbon Intensity</div>
                </div>
                <div className="text-center">
                  <div className="inline-block px-4 py-2 rounded-2xl text-sm font-semibold bg-green-100 text-green-800">
                    ${totalCost} {costUnit}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">Production Cost</div>
                </div>
              </div>
            </div>
          </div>

          <h5 className="text-sm font-semibold text-gray-900">Pathway Details</h5>
          <div className="grid grid-cols-2 gap-4">
            {details.map(({ label, value }) => (
              <DetailRow key={label} label={label} value={value} />
            ))}
          </div>
        </div>
      </AnalysisBox>
    </AnalysisContainer>
  );
};

PathwayDescription.propTypes = {
  pathway: PropTypes.object.isRequired,
};

export default PathwayDescription;
